/**
 * Foreground FCM: onMessage + Toastify + optional early token sync.
 * Loaded from index.html before main.tsx so push works like the CRA/My Vesti snippet,
 * but uses this app's Firebase config (VITE_*) and localStorage fcmRegistrationToken.
 */
import { getMessaging, onMessage, isSupported } from 'firebase/messaging'
import { app } from '@/config/firebase'
import { obtainFcmRegistrationToken } from '@/lib/webPush'
import { setFcmRegistrationToken } from '@/services/session'

declare global {
  interface Window {
    Toastify?: (options: Record<string, unknown>) => { showToast: () => void }
  }
}

function showFcmToast(body: string) {
  const Toastify = window.Toastify
  if (typeof Toastify !== 'function') return
  setTimeout(() => {
    Toastify({
      text: body || 'You have a new notification.',
      duration: 5000,
      close: true,
      gravity: 'top',
      position: 'right',
      stopOnFocus: true,
      style: {
        color: '#fff',
        background: '#272f16',
        padding: '20px 24px',
        borderRadius: '8px',
        fontSize: '16px',
        fontFamily: 'Segoe UI, sans-serif',
        boxShadow: '0 6px 12px rgba(0, 0, 0, 0.1)',
        maxWidth: '400px',
      },
      className: 'custom-toast',
      onClick: () => undefined,
    }).showToast()
  }, 500)
}

async function startFcmForeground() {
  const supported = await isSupported().catch(() => false)
  if (!supported) return

  const messaging = getMessaging(app)

  onMessage(messaging, (payload) => {
    const data = { ...(payload.data ?? {}) }
    data.foreground = 'true'

    try {
      const mc = payload.data?.message_count
      const badge = payload.data?.badge
      if (mc != null) sessionStorage.setItem('fcm_message_count', String(mc))
      if (badge != null) sessionStorage.setItem('fcm_notification_count', String(badge))
    } catch {
      /* ignore */
    }

    window.dispatchEvent(
      new CustomEvent('myCustomEvent', {
        detail: {
          messageCount: payload.data?.message_count,
          notificationCount: payload.data?.badge,
          payload,
        },
      })
    )

    window.dispatchEvent(
      new CustomEvent('fcmForegroundMessage', {
        detail: { payload, data },
      })
    )

    const body = payload.notification?.body ?? 'You have a new notification.'
    showFcmToast(body)
  })

  const token = await obtainFcmRegistrationToken()
  if (token) {
    setFcmRegistrationToken(token)
  }
}

void startFcmForeground()
